import moment from 'moment';
import { ISupportTicket, TTicketActor, TTicketPriority, TTicketState } from 'models/SupportTicket';
import { IThirdPartyCustomer } from './hooks/useCreateTicket';

export const TICKET_STATE_LABEL: Record<TTicketState, string> = {
  New: 'Mới',
  Received: 'Đã tiếp nhận',
  Processing: 'Đang xử lý',
  Postponed: 'Hẹn lại',
  Completed: 'Hoàn tất',
  Canceled: 'Đã hủy'
};

// Bảng màu tối giản: hầu hết trạng thái dùng nền/chữ trung tính (slate), chỉ giữ 1 chấm màu nhỏ
// để phân biệt nhanh; chỉ "Hoàn tất" (xanh) và "Đã hủy" (đỏ nhạt) mới có tín hiệu màu rõ, vì đó
// là 2 trạng thái kết thúc quan trọng cần nhận biết ngay, các trạng thái đang xử lý còn lại không cần nổi bật màu.
export const TICKET_STATE_COLOR: Record<TTicketState, { bg: string; text: string; dot: string }> = {
  New: { bg: '#f8fafc', text: '#475569', dot: '#f97316' },
  Received: { bg: '#f8fafc', text: '#475569', dot: '#3b82f6' },
  Processing: { bg: '#f8fafc', text: '#475569', dot: 'rgb(0, 108, 135)' },
  Postponed: { bg: '#f8fafc', text: '#475569', dot: '#6b7280' },
  Completed: { bg: '#f0fdf4', text: '#15803d', dot: '#16a34a' },
  Canceled: { bg: '#f8fafc', text: '#64748b', dot: '#dc2626' }
};

export const TICKET_STATE_ORDER: TTicketState[] = [
  'New',
  'Received',
  'Processing',
  'Postponed',
  'Completed',
  'Canceled'
];

export const TICKET_PRIORITY_LABEL: Record<TTicketPriority, string> = {
  0: 'Thấp',
  1: 'Trung bình',
  2: 'Cao',
  3: 'Khẩn cấp'
};

// Màu nền đặc dùng cho badge Ưu tiên (giống style badge Trạng thái — nền màu, chữ trắng)
export const TICKET_PRIORITY_BADGE_COLOR: Record<TTicketPriority, string> = {
  0: '#94a3b8',
  1: '#ca8a04',
  2: '#f97316',
  3: '#dc2626'
};

export const TICKET_ACTOR_LABEL: Record<TTicketActor, string> = {
  System: 'Hệ thống',
  Customer: 'Khách hàng',
  Staff: 'Nhân viên'
};

export const TICKET_ACTION_LABEL: Record<string, string> = {
  Create: 'Tạo phiếu',
  Receive: 'Tiếp nhận',
  Update: 'Cập nhật phiếu',
  Process: 'CSKH thực hiện yêu cầu',
  Postpone: 'Hẹn lại',
  Continue: 'Tiếp tục xử lý', // CSKH mở lại phiếu đang "Hẹn lại" (api/SupportTicket/Continue)
  Complete: 'Hoàn thành',
  Cancel: 'Hủy phiếu',
  NoteAfterComplete: 'Ghi chú sau hoàn thành'
};

export const TICKET_BRANCH_LABEL: Record<string, string> = {
  AD: 'Ánh Dương',
  BM: 'Bình Minh'
};

export const SEARCH_OPTION_LIST: { value: number; label: string; placeholder: string }[] = [
  { value: 0, label: 'Không dùng', placeholder: '' },
  { value: 1, label: 'Biển số xe', placeholder: 'Tìm theo biển số xe' },
  { value: 2, label: 'Nhóm hỗ trợ', placeholder: 'Tìm theo tên nhóm hỗ trợ' },
  { value: 3, label: 'Loại hỗ trợ', placeholder: 'Tìm theo tên loại hỗ trợ' },
  { value: 5, label: 'Tên khách hàng', placeholder: 'Tìm theo tên khách hàng' }
];

// Dọn chữ nhiều dòng do CSKH nhập trước khi hiển thị (không đổi dữ liệu gửi server):
// bỏ khoảng trắng cuối mỗi dòng, bỏ hẳn các dòng trống (chỉ chứa khoảng trắng), cắt đầu/cuối.
// Tránh khối chữ dài lê thê / đẩy tràn khung khi drawer hẹp.
// export const tidyMultilineText = (value?: string | null): string =>
//   (value || '')
//     .replace(/\r\n?/g, '\n')
//     .replace(/[ \t]+$/gm, '')
//     .replace(/\n{2,}/g, '\n')
//     .trim();

export const tidyMultilineText = (value?: string | null): string => {
  return (value || '').trim().replace(/\s*\n\s*\n\s*/g, '\n');
};

// Chữ KH gửi từ mobile (lời nhận xét đánh giá): hiện đúng như KH gõ — \t thành dấu cách, \n là xuống dòng
// (nơi hiển thị dùng whitespace-pre-wrap). Không gộp/bỏ dòng trống.
export const formatMobileText = (value?: string | null): string =>
  (value || '').replace(/\r\n?/g, '\n').replace(/\t/g, ' ');

export const formatTicketDate = (value?: string | null): string => {
  if (!value || value.startsWith('0001-01-01')) return '-';
  return moment(value).format('HH:mm DD/MM/YYYY');
};

// Phiếu quá hạn: còn mở (chưa Hoàn tất/Hủy) và đã qua "Ngày xử lý dự kiến"
export const isTicketOverdue = (ticket: Pick<ISupportTicket, 'targetDate' | 'state'>): boolean => {
  if (!ticket.targetDate || ticket.targetDate.startsWith('0001-01-01')) return false;
  if (ticket.state === 'Completed' || ticket.state === 'Canceled') return false;
  return moment(ticket.targetDate).isBefore(moment());
};

// Sắp xếp mặc định để CSKH ưu tiên xử lý: quá hạn lên đầu, rồi tới mức ưu tiên cao, rồi tới phiếu chờ lâu nhất
export const sortTicketsForQueue = <T extends Pick<ISupportTicket, 'targetDate' | 'state' | 'priority' | 'createDate'>>(
  tickets: T[]
): T[] => {
  return [...tickets].sort((a, b) => {
    const overdueA = isTicketOverdue(a) ? 1 : 0;
    const overdueB = isTicketOverdue(b) ? 1 : 0;
    if (overdueA !== overdueB) return overdueB - overdueA;
    if (a.priority !== b.priority) return b.priority - a.priority;
    return new Date(a.createDate).getTime() - new Date(b.createDate).getTime();
  });
};

// Hỗ trợ parseCustomerSearch.ts
export interface IParsedCustomerSearch {
  customerName?: string;
  phoneNum?: string;
}

// số ký tự tối thiểu (tính cả đầu 0 / 84) để coi là SĐT đang gõ dở
const MIN_PHONE_DIGITS = 4;
const MAX_PHONE_DIGITS = 12;

// token chỉ gồm số và ký tự phân cách hay gặp: 0909, 0909.123, +84909, 090-123
const NUMERIC_TOKEN = /^\+?[\d.-]+$/;

// export function parseCustomerSearch(input: string): IParsedCustomerSearch {
//   const raw = (input || '').trim();
//   if (!raw) return {};

//   const tokens = raw.split(/\s+/);
//   let phoneNum: string | undefined;
//   let nameTokens = tokens;

//   let i = 0;
//   while (i < tokens.length) {
//     if (!NUMERIC_TOKEN.test(tokens[i])) {
//       i++;
//       continue;
//     }

//     // gom các token số liền kề: "0909 123 456" -> "0909123456"
//     let j = i;
//     let digits = '';
//     while (j < tokens.length && NUMERIC_TOKEN.test(tokens[j])) {
//       digits += tokens[j].replace(/\D/g, '');
//       j++;
//     }

//     // 84xxxx -> 0xxxx
//     if (digits.startsWith('84')) digits = '0' + digits.slice(2);

//     const looksLikePhone =
//       digits.startsWith('0') && digits.length >= MIN_PHONE_DIGITS && digits.length <= MAX_PHONE_DIGITS;

//     if (looksLikePhone) {
//       phoneNum = digits;
//       // phần chữ trước và sau SĐT, gộp lại
//       nameTokens = [...tokens.slice(0, i), ...tokens.slice(j)];
//       break;
//     }
//     i = j;
//   }

//   const customerName = nameTokens.join(' ').replace(/\s+/g, ' ').trim();
//   return { customerName: customerName || undefined, phoneNum };
// }

export function parseCustomerSearch(input: string): IParsedCustomerSearch {
  const raw = (input || '').trim();
  if (!raw) return {};

  const tokens = raw.split(/\s+/);
  let phoneNum: string | undefined;
  let nameTokens = tokens;

  let i = 0;
  while (i < tokens.length) {
    if (!NUMERIC_TOKEN.test(tokens[i])) {
      i++;
      continue;
    }

    // gom các token số liền kề: "012 345" -> "012345"
    let j = i;
    let digits = '';
    while (j < tokens.length && NUMERIC_TOKEN.test(tokens[j])) {
      digits += tokens[j].replace(/\D/g, '');
      j++;
    }

    // Nếu chuỗi gom được hoàn toàn là số (bất kể dài hay ngắn)
    if (digits.length > 0) {
      // 84xxxx -> 0xxxx (tuỳ chọn: giữ lại nếu vẫn muốn chuẩn hóa đầu số quốc tế)
      if (digits.startsWith('84')) {
        digits = '0' + digits.slice(2);
      }

      phoneNum = digits;
      // phần chữ trước và sau số, gộp lại làm tên
      nameTokens = [...tokens.slice(0, i), ...tokens.slice(j)];
      break;
    }
    i = j;
  }

  const customerName = nameTokens.join(' ').replace(/\s+/g, ' ').trim();
  return { customerName: customerName || undefined, phoneNum };
}

export const formatCustomerLabel = (c: IThirdPartyCustomer) =>
  `${c.displayName ?? '_'}${c.phone ? ` - ${c.phone}` : ''} (${c.userName ?? '_'}${
    c.systemBranch ? ` - ${c.systemBranch}` : ''
  })`;

// ---- Vị trí xe / địa điểm hỗ trợ ----

export interface ILatLng {
  lat: number;
  lng: number;
}

const isValidLatLng = (lat: number, lng: number) =>
  Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

const LAT_LNG_PATTERNS = [
  /!3d(-?\d{1,3}(?:\.\d+)?)!4d(-?\d{1,3}(?:\.\d+)?)/, // vị trí ghim trong link place của Google Maps
  /@(-?\d{1,3}(?:\.\d+)?),(-?\d{1,3}(?:\.\d+)?)/, // tâm bản đồ: .../@10.76,106.66,17z
  /[?&](?:q|query|ll|destination|center)=(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)/,
  /^\s*(-?\d{1,3}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/ // "10.762622, 106.660172"
];

// Đọc tọa độ từ link Google Maps (dạng đầy đủ) hoặc chuỗi "lat, lng".
// Link rút gọn maps.app.goo.gl không chứa tọa độ -> trả null (trình duyệt không mở được link này để lấy tọa độ do CORS).
export const parseLatLng = (input?: string | null): ILatLng | null => {
  const raw = (input || '').trim();
  if (!raw) return null;

  let text = raw;
  try {
    text = decodeURIComponent(raw);
  } catch {
    // giữ nguyên chuỗi gốc nếu decode lỗi
  }

  for (const pattern of LAT_LNG_PATTERNS) {
    const m = text.match(pattern);
    if (!m) continue;
    const lat = Number(m[1]);
    const lng = Number(m[2]);
    if (isValidLatLng(lat, lng)) return { lat, lng };
  }
  return null;
};

export const isShortMapsLink = (input?: string | null) => /goo\.gl\/|maps\.app\.goo\.gl/i.test(input || '');

export const hasTicketLocation = (loc?: { lat?: number | null; lng?: number | null } | null): loc is ILatLng =>
  !!loc && isValidLatLng(Number(loc.lat), Number(loc.lng)) && !(Number(loc.lat) === 0 && Number(loc.lng) === 0);

export const formatLatLng = ({ lat, lng }: ILatLng) => `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

export const buildMapsUrl = ({ lat, lng }: ILatLng) => `https://www.google.com/maps?q=${lat},${lng}`;

// t=k: ảnh vệ tinh. Dạng embed này không cần API key.
export const buildMapsEmbedUrl = ({ lat, lng }: ILatLng, zoom = 17) =>
  `https://maps.google.com/maps?q=${lat},${lng}&t=k&z=${zoom}&output=embed`;
